import { ChevronDownIcon } from "@radix-ui/react-icons";
import { Book, Check } from "lucide-react";
import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useRuleSets } from "@/hooks/api/use-channel-tagging-rules";
import { usePermissions } from "@/hooks/auth/use-permissions";
import { useCustomChannelsRulesManagementEnabled } from "@/hooks/use-feature-flags";
import { useTrackedSessionStorage } from "@/hooks/use-tracked-browser-storage";

/**
 * Storage key for the selected ruleset id.
 * Uses a shared key so the selection syncs between list and report card views.
 *
 * Stored in org-scoped sessionStorage so it:
 * - Is tab-scoped (each tab has its own selection)
 * - Does not persist across sessions
 * - Syncs between custom channels list and report card views
 */
export const CUSTOM_CHANNELS_RULESET_STORAGE_KEY =
  "custom-channels::selected-ruleset-id";

/**
 * CustomChannelsRuleSetSelector displays the currently selected custom-channel ruleset
 * and allows admins with the custom-channels-rules-management flag to switch between rule sets.
 *
 * Selection is purely client-side (no server mutation). The selected ruleset id is:
 * - Stored in org-scoped sessionStorage
 * - Synced between list and report card views
 * - Sent to the backend on Custom Channels API requests
 */
export function CustomChannelsRuleSetSelector() {
  const featureEnabled = useCustomChannelsRulesManagementEnabled();
  const { isAdmin } = usePermissions();

  // Don't render if feature is disabled or user is not admin
  if (!featureEnabled || !isAdmin) {
    return null;
  }

  return <RuleSetSelectorContent />;
}

function RuleSetSelectorContent() {
  const { data: ruleSets, isLoading } = useRuleSets();

  // Persist the selected ruleset ID in org-scoped sessionStorage.
  // SessionStorage is tab-scoped, so each tab has its own selection.
  // Uses a shared key so selection syncs between list and report card views.
  // Migration: automatically migrate from old localStorage key if present.
  const [selectedRuleSetId, setSelectedRuleSetId] = useTrackedSessionStorage<
    string | null
  >(CUSTOM_CHANNELS_RULESET_STORAGE_KEY, null, {
    migration: {
      from: {
        key: "custom-channels::list::selected-ruleset-id",
        storage: "localStorage",
        organizationScoped: true,
      },
      cleanup: true,
    },
  });

  // Find the selected ruleset from the list
  const selectedRuleSet = ruleSets?.find((rs) => rs.id === selectedRuleSetId);

  // Find the default ruleset (if any)
  const defaultRuleSet = ruleSets?.find((rs) => rs.is_default);

  // Auto-select default ruleset if:
  // 1. No ruleset is selected, OR
  // 2. Selected ruleset no longer exists (was deleted)
  // If no default exists, leave unselected.
  useEffect(() => {
    if (ruleSets && ruleSets.length > 0) {
      const needsSelection = !selectedRuleSetId || !selectedRuleSet;
      if (needsSelection && defaultRuleSet) {
        setSelectedRuleSetId(defaultRuleSet.id);
      }
    }
  }, [
    selectedRuleSetId,
    selectedRuleSet,
    defaultRuleSet,
    ruleSets,
    setSelectedRuleSetId,
  ]);

  // Handle user selecting a different ruleset (pure client-side, no server mutation)
  const handleSelectRuleSet = (ruleSetId: string) => {
    setSelectedRuleSetId(ruleSetId);
  };

  // Loading state
  if (isLoading || !ruleSets) {
    return (
      <Button
        variant="outline"
        size="sm"
        disabled
        className="justify-between gap-2 bg-white dark:bg-[hsl(195_30%_12%)]"
      >
        <span className="flex items-center">
          <Book className="mr-2 h-4 w-4" />
          Loading...
        </span>
        <ChevronDownIcon className="h-4 w-4 opacity-50" />
      </Button>
    );
  }

  // No rule sets configured
  if (ruleSets.length === 0) {
    return (
      <div className="text-sm text-muted-foreground">
        No rule sets configured
      </div>
    );
  }

  const displayName = selectedRuleSet?.name || "Select ruleset";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="justify-between gap-2 bg-white dark:bg-[hsl(195_30%_12%)]"
        >
          <span className="flex items-center">
            <Book className="mr-2 h-4 w-4" />
            {displayName}
          </span>
          <ChevronDownIcon className="h-4 w-4 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        {ruleSets.map((ruleSet) => (
          <DropdownMenuItem
            key={ruleSet.id}
            onClick={() => handleSelectRuleSet(ruleSet.id)}
          >
            <div className="flex items-center justify-between w-full">
              <span>{ruleSet.name}</span>
              {selectedRuleSetId === ruleSet.id && (
                <Check className="ml-2 h-4 w-4" />
              )}
            </div>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
